import type { FormatFunction } from 'i18next';
import i18n from '../i18n';

const separators = new Map<string, string>();

/** The decimal separator of a language ("," for fr, "." for en). */
export const decimalSeparator = (language?: string): string => {
  const lng = (language || 'en').split('-')[0];
  const cached = separators.get(lng);
  if (cached) return cached;
  let separator = '.';
  try {
    separator =
      new Intl.NumberFormat(lng)
        .formatToParts(1.5)
        .find((part) => part.type === 'decimal')?.value ?? '.';
  } catch {
    separator = '.';
  }
  separators.set(lng, separator);
  return separator;
};

/**
 * A number as text in the reader's language: "8,31" in French, "8.31" in
 * English. Keeps the number's own digits (no rounding, no grouping), only
 * the decimal separator changes, so it is safe on any value.
 */
export const formatLocalizedNumber = (
  value: number,
  language?: string
): string => {
  const text = String(value);
  if (Number.isInteger(value) || !Number.isFinite(value)) return text;
  const separator = decimalSeparator(language);
  return separator === '.' ? text : text.replace('.', separator);
};

/**
 * i18next `interpolation.format` used with `alwaysFormat: true`: every
 * decimal number interpolated into a translation ("{{count}} jours") is
 * written with the language's decimal separator (QA 2026-10-02: French
 * screens showed "8.31 jours"). Other values pass through unchanged.
 */
export const formatInterpolatedValue: FormatFunction = (
  value,
  _format,
  language
) =>
  typeof value === 'number' ? formatLocalizedNumber(value, language) : value;

/**
 * A day or hour count as shown on screen: at most two decimals, written in
 * the current app language ("8,31" in French). For counts rendered in JSX;
 * a number passed to `t()` is localized by `formatInterpolatedValue`.
 */
export const formatDecimal = (value: number, language?: string): string =>
  formatLocalizedNumber(
    Math.round(value * 100) / 100,
    language ?? i18n.language
  );

/**
 * A review score as shown on screen: always one decimal, in the reader's
 * language ("2,2 / 5" in French, "2.2 / 5" in English). Never `toFixed(1)`
 * in a screen: it writes the English point everywhere (QA 2026-10-29).
 */
export const formatScore = (value: number, language?: string): string => {
  const text = (Math.round(value * 10) / 10).toFixed(1);
  const separator = decimalSeparator(language ?? i18n.language);
  return separator === '.' ? text : text.replace('.', separator);
};
